// PIN ekrani (Q-021): kim qurilmada turibdi. PIN -> (2-parol) -> ish oynasi.
// Tasdiqlanmagan qurilmada: ega yoki menejer PIN (+ parol) bilan kirib, qurilma turini tanlaydi.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { errorText } from '@onepos/client';
import { Alert, Button, Icon, SelectField, TextField } from '@onepos/ui';
import { deviceApi } from './device';
import { useKassa } from './store';
import type { LoginResult, StepStaff } from './store';
import { SyncBadge } from './SyncBadge';
import { LockSign } from './LockSign';
import { InstallApp } from './InstallApp';
import { supportUrl } from '../lib/support';

type Step =
  | { kind: 'password'; ticket: string | null; staff: StepStaff; left?: number; offline?: boolean }
  | { kind: 'setup'; ticket: string; staff: StepStaff }
  | { kind: 'approve'; ticket: string; staff: StepStaff };

function fmtUntil(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function resultText(r: LoginResult): string | undefined {
  if (r.kind === 'locked') {
    return `${r.staff?.full_name ? `${r.staff.full_name}: ` : ''}3 marta xato parol — ${fmtUntil(r.until)} gacha bloklangan. Ega yoki ONE POS yordami ochib beradi.`;
  }
  if (r.kind !== 'error') return undefined;
  if (r.code === 'OFFLINE_PASSWORD_SETUP') return 'Parol o‘rnatish uchun internet kerak.';
  if (r.code === 'OFFLINE_PASSWORD_UNKNOWN') return 'Internet yo‘q, bu xodim esa shu qurilmada parol bilan hali kirmagan.';
  if (r.code.startsWith('PASSWORD_WRONG')) return 'Parol noto‘g‘ri.';
  if (r.code === 'DEVICE_NOT_ALLOWED') return 'Bu xodimga shu qurilmada ishlashga ruxsat yo‘q. Ega “Rollar”da ruxsat berishi mumkin.';
  return errorText(r.code);
}

export function PinScreen() {
  const { boot } = useKassa();
  const [step, setStep] = useState<Step | null>(null);
  const [note, setNote] = useState<string>();
  const pending = boot?.approved === false;

  const onResult = useCallback((r: LoginResult): boolean => {
    if (r.kind === 'password' || r.kind === 'setup' || r.kind === 'approve') {
      setStep(r);
      setNote(undefined);
      return true;
    }
    if (r.kind === 'approved') {
      setStep(null);
      setNote('Qurilma tasdiqlandi. Endi bu qurilmada ishlaydigan xodim PIN kodini kiritsin.');
      return true;
    }
    if (r.kind === 'ok') {
      setStep(null);
      return true;
    }
    return false;
  }, []);

  const back = useCallback((msg?: string) => {
    setStep(null);
    setNote(msg);
  }, []);

  return (
    <div className="ks-lock">
      <div className="ks-lock__box">
        <div className="ks-lock__head">
          <div>
            {boot?.tenant.logo_url && <img className="ks-lock__logo" src={boot.tenant.logo_url} alt="" />}
            <div className="ks-lock__tenant">{boot?.tenant.name}</div>
            <div className="ks-lock__meta">{pending ? 'Yangi qurilma — tasdiqlanmagan' : `${boot?.branch.name} · ${boot?.terminal.name}`}</div>
          </div>
          <SyncBadge />
        </div>
        {step?.kind === 'password' && <PasswordStep step={step} onResult={onResult} onBack={back} />}
        {step?.kind === 'setup' && <SetupStep step={step} onResult={onResult} onBack={back} />}
        {step?.kind === 'approve' && <ApproveStep step={step} onResult={onResult} onBack={back} />}
        {!step && <PinPad pending={pending} note={note} onResult={onResult} />}
      </div>
      <a className="ks-lock__help" href={supportUrl(boot?.tenant.id)} target="_blank" rel="noopener noreferrer">
        <Icon name="send" /> PIN yoki parol bilan muammo? ONE POS yordamiga yozing
      </a>
      <InstallApp />
      <LockSign />
    </div>
  );
}

// ---------------------------------------------------------------------------
// PIN klaviaturasi
// ---------------------------------------------------------------------------
function PinPad({ pending, note, onResult }: { pending: boolean; note?: string; onResult: (r: LoginResult) => boolean }) {
  const { boot, login, message, sync, forgetDevice, refreshStaff } = useKassa();
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [shake, setShake] = useState(false);
  const [hint, setHint] = useState<string>();
  const pinRef = useRef('');

  const lengths = pending ? [4, 6] : [...new Set((boot?.staff ?? []).map((s) => s.pin_length))].sort();
  const maxLen = Math.max(4, ...lengths);
  const minLen = Math.min(...(lengths.length ? lengths : [4]));
  // ✓ doim 4 raqamdan: shu qurilmada ruxsati yo'q xodim ham sababini ko'rsin (jim qolmasin)
  const okLen = 4;
  const lengthHint = pending ? '' : maxLen > minLen ? 'PIN — 4 yoki 6 raqam' : minLen === 6 ? 'PIN — 6 raqam' : '';

  // Yangi qo'shilgan xodim besh daqiqa kutmasin
  useEffect(() => {
    if (!pending) void refreshStaff();
  }, [pending, refreshStaff]);

  const fail = useCallback((text: string) => {
    setError(text);
    setShake(true);
    setTimeout(() => {
      setShake(false);
      setPin('');
      pinRef.current = '';
    }, 450);
  }, []);

  const attempt = useCallback(
    async (value: string, final: boolean) => {
      setBusy(true);
      setError(undefined);
      try {
        const r = await login(value, final);
        if (onResult(r)) return;
        if (r.kind === 'wrong') fail(sync.online ? 'PIN noto‘g‘ri' : 'PIN noto‘g‘ri yoki bu xodim shu qurilmada hali online kirmagan');
        else if (r.kind === 'more') setHint('Davom eting yoki ✓ ni bosing');
        else fail(resultText(r) ?? 'Xato');
      } catch (e) {
        fail(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [login, fail, sync.online, onResult],
  );

  const press = useCallback(
    (d: string) => {
      if (busy) return;
      const next = (pinRef.current + d).slice(0, maxLen);
      if (next === pinRef.current) return;
      pinRef.current = next;
      setPin(next);
      setError(undefined);
      setHint(undefined);
      if (lengths.includes(next.length)) void attempt(next, next.length === maxLen);
    },
    [busy, maxLen, lengths, attempt],
  );

  const back = useCallback(() => {
    if (busy) return;
    pinRef.current = pinRef.current.slice(0, -1);
    setPin(pinRef.current);
    setHint(undefined);
  }, [busy]);

  const submit = useCallback(() => {
    if (busy || pinRef.current.length < okLen) return;
    void attempt(pinRef.current, true);
  }, [busy, attempt]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) press(e.key);
      else if (e.key === 'Backspace') back();
      else if (e.key === 'Enter') submit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [press, back, submit]);

  return (
    <>
      <h1 className="ks-lock__title">{pending ? 'Qurilmani tasdiqlang' : 'PIN kodni kiriting'}</h1>
      {pending && (
        <p className="ks-lock__sub">
          Restoran logini to‘g‘ri. Endi <b>ega yoki menejer</b> o‘z PIN kodini kiritib, bu qurilmani tasdiqlaydi.
        </p>
      )}
      {note && <Alert tone="success">{note}</Alert>}
      {message && <Alert tone="warning">{message}</Alert>}
      <div className={`ks-dots${shake ? ' ks-dots--shake' : ''}`} aria-label={`${pin.length} ta raqam kiritildi`}>
        {Array.from({ length: Math.max(minLen, pin.length) }).map((_, i) => (
          <span key={i} className={`ks-dot${i < pin.length ? ' ks-dot--on' : ''}`} />
        ))}
      </div>
      <div className="ks-lock__status" aria-live="polite">
        {busy ? 'Tekshirilmoqda…' : error ? <span className="ks-err">{error}</span> : hint ?? (sync.online ? lengthHint : 'Internet yo‘q — kassa internetsiz ishlayapti')}
      </div>
      <div className="ks-numpad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
          <button key={d} type="button" className="ks-key" onClick={() => press(d)} disabled={busy}>
            {d}
          </button>
        ))}
        <button type="button" className="ks-key ks-key--muted" onClick={back} disabled={busy} aria-label="O‘chirish">
          <Icon name="backspace" />
        </button>
        <button type="button" className="ks-key" onClick={() => press('0')} disabled={busy}>
          0
        </button>
        <button type="button" className="ks-key ks-key--ok" onClick={submit} disabled={busy || pin.length < okLen} aria-label="Kirish">
          <Icon name="check" />
        </button>
      </div>
      {pending && (
        <button type="button" className="ks-lock__link ks-linkbtn" onClick={() => void forgetDevice()}>
          Boshqa restoran logini bilan kirish
        </button>
      )}
    </>
  );
}

function StepHead({ staff, onBack }: { staff: StepStaff; onBack: () => void }) {
  return (
    <div className="ks-step__who">
      <span className="ks-step__avatar" aria-hidden="true">
        <Icon name="user" />
      </span>
      <span className="ks-step__name">{staff.full_name}</span>
      <Button variant="ghost" size="sm" onClick={onBack}>
        <Icon name="back" /> PIN ga qaytish
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2-parol
// ---------------------------------------------------------------------------
function PasswordStep({
  step,
  onResult,
  onBack,
}: {
  step: Extract<Step, { kind: 'password' }>;
  onResult: (r: LoginResult) => boolean;
  onBack: (msg?: string) => void;
}) {
  const { loginPassword } = useKassa();
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [left, setLeft] = useState<number | undefined>(step.left);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!password || busy) return;
    setBusy(true);
    setError(undefined);
    try {
      const r = await loginPassword(step, password);
      if (r.kind === 'password') {
        setLeft(r.left);
        setError('Parol noto‘g‘ri.');
        setPassword('');
      } else if (r.kind === 'locked') {
        onBack(resultText(r));
      } else if (!onResult(r)) {
        setError(resultText(r) ?? 'Xato');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="ks-step" onSubmit={(e) => void submit(e)}>
      <StepHead staff={step.staff} onBack={() => onBack()} />
      <h1 className="ks-lock__title">Parolingizni kiriting</h1>
      <TextField
        label="Parol"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoFocus
      />
      {error && (
        <Alert tone="danger">
          {error}
          {left !== undefined && left > 0 ? ` Yana ${left} ta urinish qoldi, keyin 60 daqiqa blok.` : ''}
        </Alert>
      )}
      {step.offline && <p className="ks-lock__sub">Internet yo‘q — parol shu qurilmada tekshiriladi.</p>}
      <Button variant="primary" type="submit" className="ks-wide" disabled={busy || !password}>
        {busy ? 'Tekshirilmoqda…' : 'Kirish'}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Parol o'rnatish (ega va "parol majburiy" rollar — birinchi kirishda)
// ---------------------------------------------------------------------------
function SetupStep({
  step,
  onResult,
  onBack,
}: {
  step: Extract<Step, { kind: 'setup' }>;
  onResult: (r: LoginResult) => boolean;
  onBack: (msg?: string) => void;
}) {
  const { setupPassword } = useKassa();
  const [a, setA] = useState('');
  const [b, setB] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (a.length < 6) return setError('Kamida 6 belgi.');
    if (a !== b) return setError('Parollar bir xil emas.');
    setBusy(true);
    setError(undefined);
    try {
      const r = await setupPassword(step.ticket, a);
      if (!onResult(r)) setError(resultText(r) ?? 'Xato');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="ks-step" onSubmit={(e) => void submit(e)}>
      <StepHead staff={step.staff} onBack={() => onBack()} />
      <h1 className="ks-lock__title">Shaxsiy parol o‘rnating</h1>
      <p className="ks-lock__sub">
        PIN kodingizni boshqalar ko‘rib qolishi mumkin. Parol esa faqat sizda bo‘ladi — boshqaruvga kirish shunda xavfsiz.
      </p>
      <TextField label="Yangi parol" type="password" autoComplete="new-password" value={a} onChange={(e) => setA(e.target.value)} autoFocus />
      <TextField label="Parolni takrorlang" type="password" autoComplete="new-password" value={b} onChange={(e) => setB(e.target.value)} />
      {error && <Alert tone="danger">{error}</Alert>}
      <Button variant="primary" type="submit" className="ks-wide" disabled={busy || !a || !b}>
        {busy ? 'Saqlanmoqda…' : 'Saqlash va kirish'}
      </Button>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Yangi qurilmani tasdiqlash: turi, nomi, filiali
// ---------------------------------------------------------------------------
const KINDS: { key: 'pos' | 'kds' | 'manager' | 'waiter'; title: string; text: string; name: string; icon: 'cash' | 'list' | 'home' | 'users' }[] = [
  { key: 'manager', title: 'Ega qurilmasi', text: 'Ega telefoni yoki kompyuteri: boshqaruv (ega xohlasa sotadi ham). Tarif limitiga kirmaydi.', name: 'Ega telefoni', icon: 'home' },
  { key: 'pos', title: 'Kassa', text: 'Savdo, to‘lov, smena.', name: 'Kassa', icon: 'cash' },
  { key: 'kds', title: 'Oshxona monitori', text: 'Oshpazlar uchun buyurtmalar ekrani.', name: 'Oshxona monitori', icon: 'list' },
  // Ofitsiant telefoni (Q-067): faqat "Ofitsiant ilovasi" bo'limi yoqilgan restoranda ko'rinadi
  { key: 'waiter', title: 'Ofitsiant telefoni', text: 'Zal, stolga buyurtma, hisob cheki kassada. Kassa joyini olmaydi.', name: 'Ofitsiant telefoni', icon: 'users' },
];

function guessKind(): 'pos' | 'manager' {
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent;
  return /iPhone|Android.+Mobile/i.test(ua) ? 'manager' : 'pos';
}

interface DeviceOptions {
  kinds: { kind: 'pos' | 'kds' | 'manager' | 'waiter'; max: number | null; used: number; enabled: boolean }[];
  devices: { id: string; name: string; kind: string; last_seen_at: string | null }[];
}

function ago(iso: string | null): string {
  if (!iso) return 'hech qachon';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (m < 60) return `${Math.max(m, 0)} daqiqa oldin`;
  if (m < 48 * 60) return `${Math.round(m / 60)} soat oldin`;
  return `${Math.round(m / 1440)} kun oldin`;
}

function ApproveStep({
  step,
  onResult,
  onBack,
}: {
  step: Extract<Step, { kind: 'approve' }>;
  onResult: (r: LoginResult) => boolean;
  onBack: (msg?: string) => void;
}) {
  const { boot, approveDevice } = useKassa();
  const [opts, setOpts] = useState<DeviceOptions | null>(null);
  const [kind, setKind] = useState<(typeof KINDS)[number]['key']>(guessKind);
  const [name, setName] = useState(() => KINDS.find((k) => k.key === guessKind())?.name ?? 'Kassa');
  const [touched, setTouched] = useState(false);
  const [replace, setReplace] = useState<string>('');
  const branches = boot?.branches ?? [];
  const [branch, setBranch] = useState(branches[0]?.id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let alive = true;
    deviceApi
      .rpc<DeviceOptions>('pos_device_options', { p_ticket: step.ticket })
      .then((o) => alive && setOpts(o))
      .catch(() => alive && setOpts(null));
    return () => {
      alive = false;
    };
  }, [step.ticket]);

  const info = (k: string) => opts?.kinds.find((x) => x.kind === k);
  const enabled = (k: string) =>
    opts ? info(k)?.enabled === true : k === 'waiter' ? false : k !== 'kds' || boot?.modules.includes('kds') === true;
  const full = (k: string) => {
    const i = info(k);
    return Boolean(i && i.max !== null && i.used >= i.max);
  };
  const sameKind = (opts?.devices ?? []).filter((d) => d.kind === kind);

  const pick = (k: (typeof KINDS)[number]['key']) => {
    setKind(k);
    setReplace('');
    if (!touched) setName(KINDS.find((x) => x.key === k)?.name ?? name);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || !name.trim()) return;
    if (full(kind) && !replace) {
      setError('Tarifdagi joy to‘lgan — qaysi eski qurilma o‘rniga qo‘shilishini tanlang.');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const r = await approveDevice(step.ticket, kind, name.trim(), branch || null, replace || null);
      if (!onResult(r)) setError(resultText(r) ?? 'Xato');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="ks-step" onSubmit={(e) => void submit(e)}>
      <StepHead staff={step.staff} onBack={() => onBack()} />
      <h1 className="ks-lock__title">Bu qurilma nima uchun?</h1>
      <div className="ks-kinds" role="radiogroup" aria-label="Qurilma turi">
        {KINDS.filter((k) => k.key !== 'waiter' || enabled('waiter')).map((k) => {
          const i = info(k.key);
          const on = enabled(k.key);
          return (
            <button
              key={k.key}
              type="button"
              role="radio"
              aria-checked={kind === k.key}
              disabled={!on}
              className={`ks-kind${kind === k.key ? ' ks-kind--on' : ''}`}
              onClick={() => pick(k.key)}
            >
              <Icon name={k.icon} />
              <span>
                <b>
                  {k.title}
                  {i && i.max !== null && on ? <em className="ks-kind__count"> {i.used} / {i.max}</em> : null}
                </b>
                <small>{on ? k.text : 'Tarifingizda yo‘q — ONE POS orqali ulanadi.'}</small>
              </span>
            </button>
          );
        })}
      </div>
      {full(kind) && (
        <fieldset className="ks-replace">
          <legend>Joy to‘lgan. Qaysi eski qurilma o‘rniga? (u uziladi)</legend>
          {sameKind.map((d) => (
            <label key={d.id} className="ks-replace__row">
              <input type="radio" name="replace" checked={replace === d.id} onChange={() => setReplace(d.id)} />
              <span>
                <b>{d.name}</b>
                <small>oxirgi faollik: {ago(d.last_seen_at)}</small>
              </span>
            </label>
          ))}
        </fieldset>
      )}
      <TextField
        label="Qurilma nomi"
        value={name}
        maxLength={60}
        onChange={(e) => {
          setTouched(true);
          setName(e.target.value);
        }}
      />
      {branches.length > 1 && (
        <SelectField label="Filial" value={branch} onChange={(e) => setBranch(e.target.value)} options={branches.map((b) => ({ value: b.id, label: b.name }))} />
      )}
      {error && <Alert tone="danger">{error}</Alert>}
      <Button variant="primary" type="submit" className="ks-wide" disabled={busy || !name.trim() || !enabled(kind)}>
        {busy ? 'Tasdiqlanmoqda…' : 'Tasdiqlash'}
      </Button>
    </form>
  );
}
